/**
 * @file Style visuel CENTRALISE : cel-shading (MeshToonMaterial + rampe 3 paliers partagee) et contour INVERTED-HULL (shader a epaisseur constante en pixels).
 *
 * Decision d'architecture (ARCHITECTURE.md §Rendu) : chaque maillage visible est accompagne d'une "coque" (meme geometrie,
 * normales lissees) rendue en BackSide avec un shader qui pousse les sommets le long de la normale EN ESPACE ECRAN.
 * Tous les objets (chats, armes, arbres, monstres) passent par `toonMesh()` / `outlineFor()` : jamais de contour ad hoc.
 */
import {
  BackSide,
  Color,
  DataTexture,
  Mesh,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
  ShaderMaterial,
  Vector2,
} from 'three';
import type { BufferGeometry, Object3D } from 'three';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { RENDER } from '@config/index';
import type { Disposable, ResourceScope, ResourceTracker } from '../gpu/ResourceTracker';

const OUTLINE_VERT = /* glsl */ `
uniform float uThickness;
uniform vec2 uResolution;
#include <batching_pars_vertex>
void main() {
  vec4 local = vec4(position, 1.0);
  vec3 n = normal;
#ifdef USE_BATCHING
  #include <batching_vertex>
  local = batchingMatrix * local;
  n = mat3(batchingMatrix) * n;
#endif
#ifdef USE_INSTANCING
  local = instanceMatrix * local;
  n = mat3(instanceMatrix) * n;
#endif
  vec4 clip = projectionMatrix * modelViewMatrix * local;
  vec3 nView = normalize(normalMatrix * n);
  vec2 dir = (projectionMatrix * vec4(nView, 0.0)).xy;
  float len = length(dir);
  if (len > 1e-5) clip.xy += (dir / len) * (uThickness * 2.0 / uResolution) * clip.w;
  gl_Position = clip;
}`;

const OUTLINE_FRAG = /* glsl */ `
uniform vec3 uColor;
void main() { gl_FragColor = vec4(uColor, 1.0); }`;

/** Contour a opacite variable (couronnes d'arbres geants qui deviennent translucides) : meme epaisseur, alpha pilote par `uOpacity`. */
const OUTLINE_FADE_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
void main() { gl_FragColor = vec4(uColor, uOpacity); }`;

/**
 * Couleur de sommet des munitions : r = melange principale->secondaire (0 = couleur d'instance, 1 = teinte secondaire `instanceTint`),
 * g = ombre (multiplicateur de luminosite). Permet des projectiles bicolores (poisson, missile) dans UN seul InstancedMesh par forme.
 */
const AMMO_COLOR_VERTEX = /* glsl */ `
#if defined( USE_COLOR ) && defined( USE_INSTANCING_COLOR )
  vColor = vec4( mix( instanceColor.rgb, instanceTint.rgb, color.r ) * color.g, 1.0 );
#else
  #include <color_vertex>
#endif`;

/** Bibliotheque de materiaux toon/outline partages (une instance par renderer). */
export class ToonKit {
  readonly gradient: DataTexture;
  private readonly toonCache = new Map<string, MeshToonMaterial>();
  private readonly outlineCache = new Map<string, ShaderMaterial>();
  private readonly hullCache = new WeakMap<BufferGeometry, BufferGeometry>();
  private ammoMaterial: MeshToonMaterial | null = null;
  /** Uniform partagee par TOUS les materiaux d'outline : resolution du drawing buffer (px). */
  readonly resolution = { value: new Vector2(1, 1) };

  constructor(private readonly res: ResourceTracker) {
    const steps = RENDER.toon.steps;
    const data = new Uint8Array(steps);
    for (let i = 0; i < steps; i++) data[i] = Math.round(((i + 1) / steps) * 255 * 0.85 + 38);
    this.gradient = res.track(new DataTexture(data, steps, 1, RedFormat));
    this.gradient.minFilter = NearestFilter;
    this.gradient.magFilter = NearestFilter;
    this.gradient.generateMipmaps = false;
    this.gradient.needsUpdate = true;
  }

  /** Materiau toon partage par couleur (cle = hex + options). */
  toon(color: string, opts: { emissive?: string; emissiveIntensity?: number; vertexColors?: boolean } = {}): MeshToonMaterial {
    const key = `${color}|${opts.emissive ?? ''}|${opts.emissiveIntensity ?? 0}|${opts.vertexColors ? 1 : 0}`;
    let m = this.toonCache.get(key);
    if (!m) {
      m = new MeshToonMaterial({ color: new Color(color), gradientMap: this.gradient, vertexColors: opts.vertexColors ?? false });
      if (opts.emissive) {
        m.emissive = new Color(opts.emissive);
        m.emissiveIntensity = opts.emissiveIntensity ?? 0.5;
      }
      this.res.track(m);
      this.toonCache.set(key, m);
    }
    return m;
  }

  /**
   * Materiau toon des munitions (partage) : couleur d'instance (`instanceColor`) + teinte secondaire (attribut d'instance `instanceTint`
   * porte par la geometrie), pilotees par les couleurs de sommet (voir AMMO_COLOR_VERTEX).
   */
  ammoToon(): MeshToonMaterial {
    if (!this.ammoMaterial) {
      const m = new MeshToonMaterial({ gradientMap: this.gradient, vertexColors: true });
      m.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader.replace('#include <color_pars_vertex>', '#include <color_pars_vertex>\nattribute vec3 instanceTint;').replace('#include <color_vertex>', AMMO_COLOR_VERTEX);
      };
      m.customProgramCacheKey = () => 'ammoToon';
      this.ammoMaterial = this.res.track(m);
    }
    return this.ammoMaterial;
  }

  /** Materiau de contour partage (epaisseur en px ecran, couleur RENDER.outline.color par defaut). */
  outline(thicknessPx: number = RENDER.outline.thicknessPx, color: string = RENDER.outline.color): ShaderMaterial {
    const key = `${thicknessPx}|${color}`;
    let m = this.outlineCache.get(key);
    if (!m) {
      m = new ShaderMaterial({
        vertexShader: OUTLINE_VERT,
        fragmentShader: OUTLINE_FRAG,
        side: BackSide,
        uniforms: { uThickness: { value: thicknessPx }, uColor: { value: new Color(color) }, uResolution: this.resolution },
      });
      this.res.track(m);
      this.outlineCache.set(key, m);
    }
    return m;
  }

  /**
   * Materiau de contour a opacite variable (`uniforms.uOpacity`) : un exemplaire PAR objet (non partage, suivi par le ResourceTracker),
   * a utiliser avec `transparent = true` tant que l'opacite est < 1.
   */
  fadeOutline(thicknessPx: number = RENDER.outline.thicknessPx, color: string = RENDER.outline.color): ShaderMaterial {
    const m = new ShaderMaterial({
      vertexShader: OUTLINE_VERT,
      fragmentShader: OUTLINE_FADE_FRAG,
      side: BackSide,
      transparent: true,
      depthWrite: false,
      uniforms: { uThickness: { value: thicknessPx }, uColor: { value: new Color(color) }, uOpacity: { value: 1 }, uResolution: this.resolution },
    });
    return this.res.track(m);
  }

  /** Geometrie de coque a normales LISSEES (sommets fusionnes), NON suivie ni mise en cache : l'appelant la possede. */
  buildHull(src: BufferGeometry): BufferGeometry {
    const clone = src.clone();
    clone.deleteAttribute('normal');
    clone.deleteAttribute('uv');
    for (const name of Object.keys(clone.attributes)) if (name !== 'position') clone.deleteAttribute(name);
    const g = mergeVertices(clone, 1e-3);
    clone.dispose();
    g.computeVertexNormals();
    return g;
  }

  /** Geometrie de coque a normales LISSEES (sommets fusionnes) : evite les trous du contour aux aretes vives. Mise en cache par source. */
  hullGeometry(src: BufferGeometry): BufferGeometry {
    let g = this.hullCache.get(src);
    if (!g) {
      g = this.res.track(this.buildHull(src));
      this.hullCache.set(src, g);
    }
    return g;
  }

  /** Maillage toon + sa coque de contour (enfant). La geometrie et les materiaux sont PARTAGES (pas de clone). */
  toonMesh(geometry: BufferGeometry, color: string, opts: { outlinePx?: number; emissive?: string; emissiveIntensity?: number } = {}): Mesh {
    const mesh = new Mesh(geometry, this.toon(color, opts));
    if (opts.outlinePx !== 0) mesh.add(this.outlineFor(geometry, opts.outlinePx));
    return mesh;
  }

  outlineFor(geometry: BufferGeometry, px?: number): Mesh {
    const hull = new Mesh(this.hullGeometry(geometry), this.outline(px));
    hull.name = 'outline';
    hull.renderOrder = -1;
    return hull;
  }

  /** Enregistre une ressource GPU non-toon (materiau basique additif des FX / faisceaux...) dans le ResourceTracker du renderer. */
  track<T extends Disposable>(resource: T, scope: ResourceScope = 'global'): T {
    return this.res.track(resource, scope);
  }

  /** A appeler sur resize : epaisseur constante quel que soit le DPR. */
  setResolution(widthPx: number, heightPx: number): void {
    this.resolution.value.set(widthPx, heightPx);
  }
}

/** Parcourt un sous-graphe et retire (sans dispose : ressources partagees) les enfants d'outline. */
export const stripOutlines = (root: Object3D): void => {
  const doomed: Object3D[] = [];
  root.traverse((o) => {
    if (o.name === 'outline') doomed.push(o);
  });
  for (const o of doomed) o.removeFromParent();
};
